import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, FormControl,
    FormControlLabel, InputLabel, MenuItem, Paper, Select,
    Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography,
} from "@mui/material"
import { Control, Controller, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import ComputedRow from "./ComputedRow"
import { IOrder } from "./types"

export default function SealingSection({ control }: { control: Control<IOrder> }) {
    const { t } = useTranslation()
    const amountPerMasterCase = useWatch({ control, name: "sealing_amount_per_master_case" }) as number
    const masterCaseCount = useWatch({ control, name: "sealing_master_case_count" }) as number

    return (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ mb: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Typography sx={{ fontWeight: "bold" }}>{t("orders.form.sealing")}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 2 }}>
                    <Controller control={control} name="sealing_side_bellows"
                        render={({ field }) => <TextField {...field} label={t("orders.form.sideBellows")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="sealing_bottom_bellows"
                        render={({ field }) => <TextField {...field} label={t("orders.form.bottomBellows")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="sealing_side_seal"
                        render={({ field: { value, ...field } }) => (
                            <FormControlLabel label={t("orders.form.sideSeal")} control={<Checkbox {...field} checked={value} size="small" />} />
                        )}
                    />
                    <Controller control={control} name="sealing_seal_bottom"
                        render={({ field: { value, ...field } }) => (
                            <FormControlLabel label={t("orders.form.bottomSeal")} control={<Checkbox {...field} checked={value} size="small" />} />
                        )}
                    />
                    <Controller control={control} name="sealing_seal_double_bottom"
                        render={({ field: { value, ...field } }) => (
                            <FormControlLabel label={t("orders.form.doubleBottomSeal")} control={<Checkbox {...field} checked={value} size="small" />} />
                        )}
                    />
                    <Controller control={control} name="sealing_cut_bottom"
                        render={({ field: { value, ...field } }) => (
                            <FormControlLabel label={t("orders.form.bottomCut")} control={<Checkbox {...field} checked={value} size="small" />} />
                        )}
                    />
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell variant="head">{t("orders.form.widthCms")}</TableCell>
                                <TableCell><Controller control={control} name="sealing_width" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.minWidth")}</TableCell>
                                <TableCell><Controller control={control} name="sealing_width_min" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.maxWidth")}</TableCell>
                                <TableCell><Controller control={control} name="sealing_width_max" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell variant="head">{t("orders.form.length")}</TableCell>
                                <TableCell><Controller control={control} name="sealing_length" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.minLength")}</TableCell>
                                <TableCell><Controller control={control} name="sealing_length_min" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.maxLength")}</TableCell>
                                <TableCell><Controller control={control} name="sealing_length_max" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>{t("orders.form.packaging")}</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 2 }}>
                    <Controller control={control} name="sealing_box_type"
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t("orders.form.boxes")}</InputLabel>
                                <Select {...field} label={t("orders.form.boxes")}>
                                    <MenuItem value={0}>Fardos</MenuItem>
                                    <MenuItem value={1}>12.5" × 12.5" × 6.25"</MenuItem>
                                    <MenuItem value={2}>12.5" × 12.5" × 12.5"</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller control={control} name="sealing_amount_per_package"
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t("orders.form.amountPerPkg")}</InputLabel>
                                <Select {...field} label={t("orders.form.amountPerPkg")}>
                                    <MenuItem value={0}>0</MenuItem>
                                    <MenuItem value={250}>250</MenuItem>
                                    <MenuItem value={500}>500</MenuItem>
                                    <MenuItem value={1000}>1000</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller control={control} name="sealing_packages_per_master_case_count"
                        render={({ field }) => <TextField {...field} label={t("orders.form.pkgsPerMasterCase")} size="small" type="number" fullWidth />}
                    />
                </Box>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableBody>
                            <ComputedRow label={t("orders.form.amountPerMasterCase")} value={amountPerMasterCase} unit="uds." />
                            <ComputedRow label={t("orders.form.masterCaseCount")} value={masterCaseCount} unit="uds." />
                        </TableBody>
                    </Table>
                </TableContainer>
            </AccordionDetails>
        </Accordion>
    )
}
